import { managementDraft, readManagement, managementSaved } from '../utils/managementDraft';
import { useState, useEffect, useRef, useCallback } from "react";
import { TextField, Switch, FormControlLabel, Box, Typography, MenuItem, FormControl, InputLabel, Select, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { IconButton, InputAdornment } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsLeftRight, faArrowsUpDown, faArrowUpFromBracket, faPlus, faTrash, faSpinner, faChevronDown, faPen } from "@fortawesome/free-solid-svg-icons";
import WidgetPreview from "./widgetPreview.js";
import CroppingModal from "./croppingModal.js";
import PoweredByUpgradeModal from "./poweredByUpgradeModal.js";
import InfoTooltip from "./InfoTooltip.js";
import EnforceUserDetails from "./enforceUserDetails.js";

import useUpgrade from '../hooks/useUpgrade.js';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import { guardWrite } from '../utils/permissions';
import "./widgetCustomizationTab.css";
import "./notificationCenter.css";
import { useTranslation } from "react-i18next";
import WebFont from "webfontloader";
import { FONT_OPTIONS, DEFAULT_FONT, findFontConfig } from "../constants/fonts";

// warm_lead_function's field pool for the Design tab, and each field's label per language
const leadParametersOf = (params) => (params || []).map((p) => ({
  id: p.id,
  name: p.name,
  description: p.description,
  user_display: p.user_display || "",
  user_display_i18n: p.user_display_i18n || null,
}));
const fieldLabelsOf = (params) => Object.fromEntries(
  (params || []).filter((p) => p.user_display_i18n?.labels).map((p) => [p.id, { ...p.user_display_i18n.labels }]));

const ACCORDION_SX = {
  background: "transparent",
  boxShadow: "none",
  border: "none",
  borderRadius: 0,
  "&:before": { display: "none" },
  marginTop: 0,
  marginBottom: "16px",
  paddingBottom: "16px",
  borderBottom: "1px solid #e0e0e0",
  "&.Mui-expanded": {
    marginTop: 0,
    marginBottom: "16px",
  },
  "&.widget-accordion-last": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
    "&.Mui-expanded": {
      marginBottom: 0,
    },
  },
};

const ACCORDION_SUMMARY_SX = {
  padding: 0,
  minHeight: "unset",
  transition: "margin-bottom 0.3s ease",
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "&.Mui-expanded": { margin: 0 },
  },
  "&.Mui-expanded": { minHeight: "unset", marginBottom: "10px" },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#5616ea",
    fontSize: "18px",
  },
};

const ACCORDION_DETAILS_SX = {
  padding: 0,
  paddingTop: "8px",
};

const WidgetCustomizationTab = ({ botId, onWidgetUpdated, onUnsavedStateChange, onUnsavedHandlersChange }) => {
  const { t } = useTranslation();
  const management = useRef(managementDraft());
  const { registerUnsavedChangesGuard } = useUnsavedChanges();
  // Same signals as the agent wizard (AgentCreation isWebflow).
  const { bots = [] } = useSelectedBot();
  const thisBot = bots.find((b) => String(b.id) === String(botId));
  const isWebflow = !!(thisBot?.webflow_hosted || thisBot?.webflow_connected || localStorage.getItem('webflow_connected') === '1');
  const [loading, setLoading] = useState(true);
  const iconInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  // Holds the ORIGINAL (uncropped) data URL per slot for the current session
  // so the user can re-open the cropper and adjust the crop on the full image.
  const originalImagesRef = useRef({ icon: null, logo: null, avatar: null });
  // Last crop rectangle (% units) per slot, so re-editing restores the user's
  // previous selection instead of the default centered 90% crop.
  const lastCropRef = useRef({ icon: null, logo: null, avatar: null });
  const [cropperInitialCrop, setCropperInitialCrop] = useState(null);
  const [currentCroppingTarget, setCurrentCroppingTarget] = useState(null);
  const [tempFile, setTempFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [uploadedIconFile, setUploadedIconFile] = useState(null);
  const [uploadedLogoFile, setUploadedLogoFile] = useState(null);
  const [uploadedAvatarFile, setUploadedAvatarFile] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [voiceUpgradeOpen, setVoiceUpgradeOpen] = useState(false);
  const [bot, setBot] = useState([]);
  const defaultWidgetStyles = {
    themeColor: "#000000ff",
    headerColor: "#ffffff",
    pulsing: false,
    icon: false,
    hoverColor: "#232323ff",
    width: 400,
    height: 550,
    logo: false,
    avatar: false,
    welcomeMessages: ["Hi, how can I assist you?"],
    predefinedQuestions: [""],
    popUpMessagesActive: false,
    popUpDelaySeconds: 5,
    widgetBorderRadius: 50,
    fontFamily: DEFAULT_FONT.value,
    widgetSize: 60,
    widgetHorizontalAlignment: "left",
    widgetVerticalAlignment: "bottom",
    headerText: "Chat with us",
    inputPlaceholder: "Type your message...",
    removePoweredBy: false,
    customBrandingText: "",
    openLinksInNewTab: true,
    voiceInput: false,
    // Enforce User Details (widget pre-chat gate). Folded into widgetStyles so
    // changes mark the tab dirty and persist via the shared Save button like
    // every other setting. The field DEFINITIONS live in `leadParameters`
    // (structural, edited immediately); these two are just the gate switch and
    // which field ids it requires.
    requirePreChat: false,
    requiredFields: [],
    // Multilingual form (18.10.2026): the "chat in any language" note, and each field's label per language
    // ({paramId: {lang: text}}) – made by the backend, corrected here, saved with the shared Save button.
    anyLanguageNote: false,
    fieldLabels: {},
  };
  const [initialWidgetStyles, setInitialWidgetStyles] = useState(null);
  const [widgetStyles, setWidgetStyles] = useState(defaultWidgetStyles);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Lead-capture parameter pool (Name / Email / custom). Structural catalog —
  // add/delete hit the API immediately; only the gate's selection is dirty.
  const [leadParameters, setLeadParameters] = useState([]);
  const [labelsPending, setLabelsPending] = useState(false);

  // Persist accordion open/close state per bot across tab switches.
  // Default: first accordion (header text) open if no saved state.
  const accordionStorageKey = botId ? `widgetAccordions:${botId}` : null;
  const DEFAULT_EXPANDED_ACCORDIONS = {
    headerText: true,
    welcomeMessages: true,
    popUps: true,
    predefinedQuestions: true,
    inputPlaceholder: true,
    colors: true,
    branding: true,
    widget: true,
    linkBehavior: true,
  };
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    if (!accordionStorageKey) return { ...DEFAULT_EXPANDED_ACCORDIONS };
    try {
      const raw = localStorage.getItem(accordionStorageKey);
      if (raw) return JSON.parse(raw) || { ...DEFAULT_EXPANDED_ACCORDIONS };
    } catch (_e) { /* noop */ }
    return { ...DEFAULT_EXPANDED_ACCORDIONS };
  });

  useEffect(() => {
    if (!accordionStorageKey) return;
    try {
      localStorage.setItem(accordionStorageKey, JSON.stringify(expandedAccordions));
    } catch (_e) { /* noop */ }
  }, [accordionStorageKey, expandedAccordions]);

  const toggleAccordion = (key) => (_e, isExpanded) => {
    setExpandedAccordions((prev) => ({ ...prev, [key]: isExpanded }));
  };

  const MAX_HEADER_CHARS = 60;
  const MAX_INPUT_PLACEHOLDER_CHARS = 40;
  const MAX_WELCOME_CHARS = 300;
  const MAX_PREDEFINED_CHARS = 40;
  const headerCharsUsed = widgetStyles.headerText.length;
  const headerAtLimit = headerCharsUsed >= MAX_HEADER_CHARS;
  const inputPlaceholderCharsUsed = widgetStyles.inputPlaceholder.length;
  const inputPlaceholderAtLimit = inputPlaceholderCharsUsed >= MAX_INPUT_PLACEHOLDER_CHARS;

  // `quiet`: no loading view (after a save); `rebase`: the fetched state becomes the saved baseline
  const fetchWidgetConfiguration = useCallback(
    async (botId, { quiet = false, rebase = false } = {}) => {
      if (!quiet) setLoading(true);
      try {
        const res = await readManagement(fetch, 'widget', botId, 'widget_configuration');
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();
        management.current.loaded(botId, data);

        const fetchedStyles = {
          width: data.chat_window_width ?? 400,
          height: data.chat_window_height ?? 550,
          themeColor: data.theme_color,
          hoverColor: data.button_hover_color,
          pulsing: data.pulsing,
          headerColor: data.header_font_color,
          welcomeMessages: data.welcome_message
            ? Array.isArray(data.welcome_message)
              ? data.welcome_message
              : [data.welcome_message]
            : [""],
          predefinedQuestions: (() => {
            const pq = data.predefined_questions;
            if (!pq) return [];
            if (Array.isArray(pq)) return pq.length === 1 && pq[0] === "" ? [] : pq;
            return pq === "" ? [] : [pq];
          })(),
          popUpMessagesActive: data.pop_up_messages ?? false,
          popUpDelaySeconds: data.pop_up_delay_seconds ?? 5,
          widgetBorderRadius: data.widget_border_radius ?? 50,
          widgetSize: data.widget_size ?? 70,
          icon: data.widget_icon_path || false,
          logo: data.header_icon_path || false,
          widgetHorizontalAlignment: data.widget_horizontal_alignment,
          widgetVerticalAlignment: data.widget_vertical_alignment,
          headerText: data.header_text,
          fontFamily: data.font_family || DEFAULT_FONT.value,
          inputPlaceholder: data.input_placeholder || 'Type your message...',
          websiteDesktopScreenshotImgPath: data.website_desktop_screenshot_img_path,
          websiteMobileScreenshotImgPath: data.website_mobile_screenshot_img_path,
          avatar: data.avatar_icon_path || false,
          removePoweredBy: data.remove_powered_by ?? false,
          customBrandingText: data.custom_branding_text ?? "",
          openLinksInNewTab: data.open_links_in_new_tab ?? true,
          voiceInput: data.voice_input ?? false,
          requirePreChat: false,
          requiredFields: [],
          anyLanguageNote: false,
          fieldLabels: {},
        };

        // Enforce User Details lives on the same widget_config but is surfaced
        // through the warm_lead_function endpoint (collection_mode +
        // required_fields, plus the lead-parameter pool). Merge it in here so
        // it shares this tab's single fetch / dirty / save lifecycle.
        try {
          const wlRes = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}`
          );
          if (wlRes.ok) {
            const wl = await wlRes.json();
            fetchedStyles.requirePreChat = (wl.collection_mode || "optional") === "required";
            fetchedStyles.requiredFields = wl.required_fields || [];
            fetchedStyles.anyLanguageNote = !!wl.any_language_note;
            fetchedStyles.fieldLabels = fieldLabelsOf(wl.bot_function_parameters);
            setLeadParameters(leadParametersOf(wl.bot_function_parameters));
            // labels the backend is still translating (it starts on this read): picked up shortly after
            setLabelsPending((wl.bot_function_parameters || []).some((p) => !p.user_display_i18n));
          }
        } catch (wlErr) {
          console.error("Error fetching enforce-details config:", wlErr);
        }

        // the baseline only from the first load; functional, so this loader does not depend on it – with the
        // baseline as a dependency, every change of it (a field added or deleted, labels arriving) re-ran the
        // load effect: the whole tab reloaded, jumped to the top and lost unsaved selections (18.10.2026)
        setInitialWidgetStyles((prev) => (rebase || !prev ? fetchedStyles : prev));
        setWidgetStyles(fetchedStyles);
      } catch (err) {
        console.error("Error fetching widget configuration:", err);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    []
  );

  const handleScreenshotRefresh = useCallback(async (url) => {
    if (!botId) return;
    await fetchWidgetConfiguration(botId);
    if (onWidgetUpdated) await onWidgetUpdated();
  }, [botId, fetchWidgetConfiguration, onWidgetUpdated]);

  const fetchBots = useCallback(async (botId) => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${botId}&include_plan=true`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const data = await res.json();
      setBot(data);
    } catch (err) {
      console.error("Error fetching bots:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (botId) {
      fetchWidgetConfiguration(botId);
      fetchBots(botId);
    }
  }, [botId, fetchWidgetConfiguration, fetchBots]);

  const checkForUnsavedChanges = useCallback(() => {
    if (!initialWidgetStyles) return;
    setHasUnsavedChanges(
      JSON.stringify(initialWidgetStyles) !== JSON.stringify(widgetStyles)
    );
  }, [initialWidgetStyles, widgetStyles]);

  useEffect(() => {
    checkForUnsavedChanges();
  }, [widgetStyles, checkForUnsavedChanges]);

  useEffect(() => {
    if (onUnsavedStateChange) onUnsavedStateChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedStateChange]);

  useEffect(() => {
    const fontConfig = findFontConfig(widgetStyles.fontFamily);
    if (fontConfig?.google) {
      WebFont.load({
        google: { families: [fontConfig.google] },
      });
    }
  }, [widgetStyles.fontFamily]);

  const selectedFontConfig = findFontConfig(widgetStyles.fontFamily);
  const customFontOption =
    widgetStyles.fontFamily && !selectedFontConfig
      ? {
          label: `${widgetStyles.fontFamily} (custom)`,
          value: widgetStyles.fontFamily,
        }
      : null;
  const availableFontOptions = customFontOption
    ? [...FONT_OPTIONS, customFontOption]
    : FONT_OPTIONS;

  const handleStyleChange = (key, value) =>
    setWidgetStyles((prev) => ({ ...prev, [key]: value }));

  // ── Enforce User Details handlers ──────────────────────────────────────
  // Translations that arrived after the first read (the backend makes them in the background): taken over into
  // the loaded AND the current state for fields the owner has not touched yet, so nothing shows as unsaved.
  const refreshFieldLabels = async (forBot) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${forBot}`);
      if (!res.ok) return;
      const wl = await res.json();
      const fresh = fieldLabelsOf(wl.bot_function_parameters);
      setLeadParameters(leadParametersOf(wl.bot_function_parameters));
      const merge = (styles) => {
        if (!styles) return styles;
        const labels = { ...(styles.fieldLabels || {}) };
        Object.entries(fresh).forEach(([id, l]) => { if (!labels[id] || !Object.keys(labels[id]).length) labels[id] = l; });
        return { ...styles, fieldLabels: labels };
      };
      setInitialWidgetStyles(merge);
      setWidgetStyles(merge);
    } catch { /* the owner's labels still work without translations */ }
  };

  useEffect(() => {
    if (!labelsPending || !botId) return undefined;
    const timer = setTimeout(() => { setLabelsPending(false); refreshFieldLabels(botId); }, 6000);
    return () => clearTimeout(timer);
  }, [labelsPending, botId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleAnyLanguage = (on) => setWidgetStyles((prev) => ({ ...prev, anyLanguageNote: on }));

  const handleEditFieldLabel = (paramId, lang, text) =>
    setWidgetStyles((prev) => ({
      ...prev,
      fieldLabels: { ...prev.fieldLabels, [paramId]: { ...(prev.fieldLabels?.[paramId] || {}), [lang]: text } },
    }));

  // Toggle + field selection are plain style changes (dirty → saved on Save).
  const handleToggleRequirePreChat = (enabled) =>
    setWidgetStyles((prev) => ({
      ...prev,
      requirePreChat: enabled,
      // First time the gate is switched on with nothing selected, require every
      // field by default (matches the widget falling back to no-gate otherwise).
      requiredFields:
        enabled && (prev.requiredFields || []).length === 0
          ? leadParameters.map((p) => p.id)
          : prev.requiredFields,
    }));

  const handleToggleRequiredField = (paramId) =>
    setWidgetStyles((prev) => {
      const current = prev.requiredFields || [];
      return {
        ...prev,
        requiredFields: current.includes(paramId)
          ? current.filter((id) => id !== paramId)
          : [...current, paramId],
      };
    });

  // Add / delete a field is a structural catalog op — it creates/removes a real
  // parameter, so it hits the API immediately (guarded for read-only teammates)
  // rather than waiting for Save.
  const handleAddLeadParameter = async (formData) => {
    if (!guardWrite()) return;
    // shown at once with a spinner in place of its checkbox while the backend saves it and translates its label
    // (a few seconds); then replaced by the real field, already selected (Robert, 18.10.2026)
    const pendingId = `pending-${Date.now()}`;
    setLeadParameters((prev) => [...prev, {
      id: pendingId, name: formData.name, description: formData.description,
      user_display: formData.user_display || "", user_display_i18n: null, pending: true,
    }]);
    try {
      const beforeIds = new Set(leadParameters.map((p) => p.id));
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}/parameter`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            user_display: formData.user_display || null,
            type: "string",
            required: true,
            active: true,
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      // Refetch the pool to learn the new id, then auto-require the new field
      // (a dirty change the user persists with Save).
      const wlRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}`
      );
      if (!wlRes.ok) throw new Error(`HTTP error! Status: ${wlRes.status}`);
      const wl = await wlRes.json();
      const params = leadParametersOf(wl.bot_function_parameters);
      const added = params.filter((p) => !beforeIds.has(p.id)).map((p) => p.id);
      const addedLabels = Object.fromEntries(
        Object.entries(fieldLabelsOf(wl.bot_function_parameters)).filter(([id]) => added.includes(Number(id))));
      setLeadParameters(params);
      setWidgetStyles((prev) => ({
        ...prev,
        requiredFields: [...new Set([...(prev.requiredFields || []), ...added])],
        fieldLabels: { ...(prev.fieldLabels || {}), ...addedLabels },
      }));
      setInitialWidgetStyles((prev) => (prev ? { ...prev, fieldLabels: { ...(prev.fieldLabels || {}), ...addedLabels } } : prev));
    } catch (err) {
      console.error("Error adding lead parameter:", err);
      setLeadParameters((prev) => prev.filter((p) => p.id !== pendingId));
    }
  };

  const handleDeleteLeadParameter = async (paramId) => {
    if (!guardWrite()) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/parameter/${paramId}?bot_id=${botId}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      setLeadParameters((prev) => prev.filter((p) => p.id !== paramId));
      // Drop the id from BOTH current and initial selection so removing a field
      // never registers as a phantom unsaved change.
      const withoutLabels = (labels) => Object.fromEntries(
        Object.entries(labels || {}).filter(([id]) => Number(id) !== Number(paramId)));
      setWidgetStyles((prev) => ({
        ...prev,
        requiredFields: (prev.requiredFields || []).filter((id) => id !== paramId),
        fieldLabels: withoutLabels(prev.fieldLabels),
      }));
      setInitialWidgetStyles((prev) =>
        prev
          ? { ...prev, requiredFields: (prev.requiredFields || []).filter((id) => id !== paramId),
              fieldLabels: withoutLabels(prev.fieldLabels) }
          : prev
      );
    } catch (err) {
      console.error("Error deleting lead parameter:", err);
    }
  };

  const handleWelcomeChange = (idx, value) => {
    const arr = [...widgetStyles.welcomeMessages];
    arr[idx] = value;
    handleStyleChange("welcomeMessages", arr);
  };

  const addWelcomeMessage = () =>
    handleStyleChange("welcomeMessages", [...widgetStyles.welcomeMessages, ""]);

  const removeWelcomeMessage = (idx) => {
    const arr = widgetStyles.welcomeMessages.filter((_, i) => i !== idx);
    handleStyleChange("welcomeMessages", arr.length ? arr : [""]);
  };

  const handlePredefinedChange = (idx, value) => {
    const arr = [...widgetStyles.predefinedQuestions];
    arr[idx] = value;
    handleStyleChange("predefinedQuestions", arr);
  };

  const addPredefinedQuestion = () => {
    if (widgetStyles.predefinedQuestions.length >= 3) return;
    handleStyleChange("predefinedQuestions", [
      ...widgetStyles.predefinedQuestions,
      "",
    ]);
  };

  const removePredefinedQuestion = (idx) => {
    const arr = widgetStyles.predefinedQuestions.filter((_, i) => i !== idx);
    handleStyleChange("predefinedQuestions", arr);
  };

  const handleUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setTempFile(file);
    setCurrentCroppingTarget(type);
    // Fresh upload -> forget any previous crop selection for this slot.
    lastCropRef.current[type] = null;
    setCropperInitialCrop(null);
    const reader = new FileReader();
    reader.onload = () => {
      // Remember the uncropped source so subsequent "edit" passes can re-crop
      // from the original instead of the already-cropped result.
      originalImagesRef.current[type] = reader.result;
      setImageSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Resolve a stored icon/logo/avatar value to a usable <img src>.
  // - Empty / false → null
  // - data: URL → returned as-is
  // - Anything else → treated as a backend-relative path or absolute URL.
  const resolveStoredImageSrc = (value) => {
    if (!value) return null;
    if (typeof value !== "string") return null;
    if (value.startsWith("data:")) return value;
    if (/^https?:\/\//i.test(value)) return value;
    const base = process.env.REACT_APP_API_BASE_URL || "";
    return `${base}${value.startsWith("/") ? value.slice(1) : value}`;
  };

  // Re-open the cropper for an already-uploaded or saved image.
  // Prefer the original (uncropped) source captured during upload so the user
  // can re-crop the full picture, not only the previously cropped region.
  const handleEditExistingFile = (type) => {
    const original = originalImagesRef.current[type];
    const fallback = resolveStoredImageSrc(widgetStyles[type]);
    const src = original || fallback;
    if (!src) return;
    setTempFile(null); // editing — no new File, keep any existing uploadedXFile name
    setCropperInitialCrop(lastCropRef.current[type] || null);
    setImageSrc(src);
    setCurrentCroppingTarget(type);
  };

  const handleCancelCrop = () => {
    setTempFile(null);
    setImageSrc(null);
    setCurrentCroppingTarget(null);
    setCropperInitialCrop(null);
    if (iconInputRef.current) iconInputRef.current.value = null;
    if (logoInputRef.current) logoInputRef.current.value = null;
    if (avatarInputRef.current) avatarInputRef.current.value = null;
  };

  const handleConfirmCrop = (croppedBase64, percentCrop) => {
    const target = currentCroppingTarget;
    if (target && percentCrop) {
      lastCropRef.current[target] = percentCrop;
    }
    if (target === "icon") {
      setWidgetStyles((p) => ({ ...p, icon: croppedBase64 }));
      if (tempFile) setUploadedIconFile(tempFile);
      if (iconInputRef.current) iconInputRef.current.value = null;
    } else if (target === "logo") {
      setWidgetStyles((p) => ({ ...p, logo: croppedBase64 }));
      if (tempFile) setUploadedLogoFile(tempFile);
      if (logoInputRef.current) logoInputRef.current.value = null;
    } else if (target === "avatar") {
      setWidgetStyles((p) => ({ ...p, avatar: croppedBase64 }));
      if (tempFile) setUploadedAvatarFile(tempFile);
      if (avatarInputRef.current) avatarInputRef.current.value = null;
    }
    setTempFile(null);
    setImageSrc(null);
    setCurrentCroppingTarget(null);
    setCropperInitialCrop(null);
  };

  const handleRemoveFile = (type) => {
    originalImagesRef.current[type] = null;
    lastCropRef.current[type] = null;
    if (type === "icon") {
      setWidgetStyles((p) => ({ ...p, icon: false }));
      setUploadedIconFile(null);
      if (iconInputRef.current) iconInputRef.current.value = null;
    } else if (type === "logo") {
      setWidgetStyles((p) => ({ ...p, logo: false }));
      setUploadedLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = null;
    } else if (type === "avatar") {
      setWidgetStyles((p) => ({ ...p, avatar: false }));
      setUploadedAvatarFile(null);
      if (avatarInputRef.current) avatarInputRef.current.value = null;
    }
  };

  const base64ToFile = (base64, filename) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    return new File([u8], filename, { type: mime });
  };

  const uploadImage = async (base64, type) => {
    const fd = new FormData();
    fd.append("file", base64ToFile(base64, `${type}-${Date.now()}.png`));
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}upload_image/`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) throw new Error(`Failed to upload ${type}. Status: ${res.status}`);
    const j = await res.json();
    return j.file_path;
  };

  const sanitizePredefined = (arr) =>
    Array.isArray(arr) && arr.length === 0 ? [""] : arr;

  const handleSaveWidgetConfiguration = async () => {
    if (!botId) return;
    // Read-only teammates: revert local edits, surface the shared
    // permission modal, and return `undefined` (NOT `false`) so the
    // unsaved-changes modal closes and navigation continues normally.
    // Returning `false` here would leave the unsaved-changes prompt
    // stuck open and block the user from moving on.
    if (!guardWrite(() => handleDiscardChanges())) return;
    try {
      let iconPath = widgetStyles.icon;
      let logoPath = widgetStyles.logo;
      let avatarPath = widgetStyles.avatar;
      if (iconPath && iconPath.startsWith("data:image/"))
        iconPath = await uploadImage(iconPath, "icon");
      if (logoPath && logoPath.startsWith("data:image/"))
        logoPath = await uploadImage(logoPath, "logo");
      if (avatarPath && avatarPath.startsWith("data:image/"))
        avatarPath = await uploadImage(avatarPath, "avatar");

      const payload = {
        theme_color: widgetStyles.themeColor,
        button_hover_color: widgetStyles.hoverColor,
        pulsing: widgetStyles.pulsing,
        chat_window_width: widgetStyles.width,
        chat_window_height: widgetStyles.height,
        header_font_color: widgetStyles.headerColor,
        welcome_message: widgetStyles.welcomeMessages,
        predefined_questions: sanitizePredefined(widgetStyles.predefinedQuestions),
        pop_up_messages: widgetStyles.popUpMessagesActive,
        pop_up_delay_seconds: widgetStyles.popUpDelaySeconds,
        widget_horizontal_alignment: widgetStyles.widgetHorizontalAlignment,
        widget_vertical_alignment: widgetStyles.widgetVerticalAlignment,
        widget_border_radius: widgetStyles.widgetBorderRadius,
        widget_size: widgetStyles.widgetSize,
        widget_icon_path: iconPath || "",
        header_icon_path: logoPath || "",
        avatar_icon_path: avatarPath || "",
        header_text: widgetStyles.headerText,
        font_family: widgetStyles.fontFamily,
        input_placeholder: widgetStyles.inputPlaceholder || 'Type your message...',
        remove_powered_by: widgetStyles.removePoweredBy,
        custom_branding_text: widgetStyles.customBrandingText || "",
        open_links_in_new_tab: widgetStyles.openLinksInNewTab,
        voice_input: widgetStyles.voiceInput,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${botId}`,
        management.current.request(botId, payload)
      );
      await managementSaved(res, management.current, botId, t);

      // Persist the Enforce User Details gate (collection_mode +
      // required_fields → widget_config) alongside the widget styles, so the
      // single Save button covers both.
      const wlRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            collection_mode: widgetStyles.requirePreChat ? "required" : "optional",
            required_fields: widgetStyles.requiredFields || [],
            any_language_note: !!widgetStyles.anyLanguageNote,
            // only the fields whose labels the owner changed
            bot_function_parameters: Object.entries(widgetStyles.fieldLabels || {})
              .filter(([id, labels]) => JSON.stringify(labels) !== JSON.stringify(initialWidgetStyles?.fieldLabels?.[id]))
              .map(([id, labels]) => ({ id: Number(id), user_display_i18n: { labels } })),
          }),
        }
      );
      if (!wlRes.ok) throw new Error(`Save failed: ${wlRes.status}`);

      setInitialWidgetStyles(widgetStyles);
      setHasUnsavedChanges(false);
      // the stored state (uploaded image URLs, saved labels) as the new baseline – quietly, the tab stays put
      await fetchWidgetConfiguration(botId, { quiet: true, rebase: true });
      if (onWidgetUpdated) await onWidgetUpdated();
      return true;
    } catch (err) {
      console.error("Save error:", err);
      // Don't dispatch a manual `api-error` here — the global fetch
      // interceptor in `src/index.js` already routes 403s to the unified
      // permission modal and other 4xx/5xx to the generic error modal.
      // A second hardcoded `status: 500` dispatch produced a misleading
      // "Server Error" overlay even on plain 403 read-only refusals.
      return false;
    }
  };

  const handleDiscardChanges = () => {
    if (!initialWidgetStyles) return;
    setWidgetStyles({ ...initialWidgetStyles });
    setUploadedIconFile(null);
    setUploadedLogoFile(null);
    setUploadedAvatarFile(null);
    if (iconInputRef.current) iconInputRef.current.value = null;
    if (logoInputRef.current) logoInputRef.current.value = null;
    if (avatarInputRef.current) avatarInputRef.current.value = null;
    setHasUnsavedChanges(false);
  };

  // Keep latest handlers in a ref so guard registration doesn't re-run on every keystroke.
  const unsavedHandlersRef = useRef({ save: handleSaveWidgetConfiguration, discard: handleDiscardChanges });
  unsavedHandlersRef.current = { save: handleSaveWidgetConfiguration, discard: handleDiscardChanges };

  useEffect(() => {
    if (!onUnsavedHandlersChange) return undefined;
    onUnsavedHandlersChange({
      save: (...args) => unsavedHandlersRef.current.save(...args),
      discard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
    return () => onUnsavedHandlersChange(null);
  }, [onUnsavedHandlersChange]);

  useEffect(() => {
    return registerUnsavedChangesGuard(`widget-customization-${botId}`, {
      when: hasUnsavedChanges,
      onSave: (...args) => unsavedHandlersRef.current.save(...args),
      onDiscard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
  }, [botId, hasUnsavedChanges, registerUnsavedChangesGuard]);

  const onUpgradeComplete = async () => {
    setLoading(true);
    await fetchWidgetConfiguration(botId);
    await fetchBots(botId);
    setLoading(false);
  };

  const { handleUpgradeClick: upgradeClickRaw, UpgradeModal } = useUpgrade({ onComplete: onUpgradeComplete });

  // Voice input is included from Boost up (the backend checks the same, controller/voice_input.py).
  const voicePlanOk = ["Boost", "Ultimo"].includes(bot?.subscription_plan?.name);

  const handleUpgradeClick = async (bot) => {
    setUpgradeModalOpen(false);
    await upgradeClickRaw(bot);
  };

  if (loading)
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );

  return (
    <div className="customization-container">
      {/* ---------- cropping modal ---------- */}
      <CroppingModal
        isOpen={Boolean(currentCroppingTarget)}
        src={imageSrc}
        initialCrop={cropperInitialCrop}
        onCancel={handleCancelCrop}
        onConfirm={handleConfirmCrop}
      />

      {/* ---------- powered by upgrade modal ---------- */}
      <PoweredByUpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        bot={bot}
        onUpgrade={() => handleUpgradeClick(bot)}
      />
      {/* ---------- voice input upgrade modal (the same, with its own words) ---------- */}
      <PoweredByUpgradeModal
        open={voiceUpgradeOpen}
        onClose={() => setVoiceUpgradeOpen(false)}
        bot={bot}
        title={t("voice_upgrade_title")}
        description={t("voice_upgrade_description")}
        onUpgrade={() => { setVoiceUpgradeOpen(false); handleUpgradeClick(bot); }}
      />

      <div className="options-container">
        <Accordion expanded={!!expandedAccordions.headerText} onChange={toggleAccordion("headerText")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("header_text")}
              <InfoTooltip text={t("widget_header_text_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>

        {/* ---------- header text ---------- */}
        <div className="control-group" style={{marginBottom: '0'}}>
          <TextField
            id="header-text-input"
            label={t("edit_header_text")}
            value={widgetStyles.headerText}
            onChange={(e) => handleStyleChange("headerText", e.target.value)}
            multiline
            variant="outlined"
            inputProps={{ maxLength: MAX_HEADER_CHARS }}
            helperText={`${headerCharsUsed}/${MAX_HEADER_CHARS}`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                mr: 0,
                color: headerAtLimit ? "error.main" : "text.secondary",
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                border: "1px solid #515151",
              },
              "& .MuiInputLabel-root": {
                color: "gray",
                "&.Mui-focused": { color: "#5616ea" },
              },
            }}
          />
        </div>

          </AccordionDetails>
        </Accordion>

        {/* ---------- welcome messages (array) ---------- */}
        <Accordion expanded={!!expandedAccordions.welcomeMessages} onChange={toggleAccordion("welcomeMessages")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("edit_welcome_messages")}
              <InfoTooltip text={t("widget_welcome_messages_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <div className="control-group">

          <div className="welcome-messages-parent">
            {widgetStyles.welcomeMessages.map((msg, idx) => (
              <Box key={idx} marginBottom={-0.8}>
                <TextField
                  label={`${t("welcome_message")} ${idx + 1}`}
                  value={msg}
                  onChange={(e) => handleWelcomeChange(idx, e.target.value)}
                  fullWidth
                  multiline
                  variant="outlined"
                  inputProps={{ maxLength: MAX_WELCOME_CHARS }}
                  helperText={`${msg.length}/${MAX_WELCOME_CHARS}`}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: "right",
                      mr: 0,
                      color:
                        msg.length >= MAX_WELCOME_CHARS
                          ? "error.main"
                          : "text.secondary",
                    },
                  }}
                  InputProps={{
                    endAdornment:
                      widgetStyles.welcomeMessages.length > 1 ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t("remove")}
                            onClick={() => removeWelcomeMessage(idx)}
                            edge="end"
                            size="small"
                            sx={{ color: "text.secondary" }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      border: "1px solid #515151",
                    },
                    "& .MuiInputLabel-root": {
                      color: "gray",
                      "&.Mui-focused": { color: "#5616ea" },
                    },
                  }}
                />
              </Box>
            ))}
          </div>

          <button
            onClick={addWelcomeMessage}
            className="add-email-button"
            title={t("add")}
            disabled={widgetStyles.welcomeMessages.length >= 3}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          {widgetStyles.welcomeMessages.length >= 3 && (
            <Typography variant="caption" color="textSecondary">
              {t("max_three_reached")}
            </Typography>
          )}
        </div>

          </AccordionDetails>
        </Accordion>

        {/* ---------- pop-up messages toggle + delay ---------- */}
        <Accordion expanded={!!expandedAccordions.popUps} onChange={toggleAccordion("popUps")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("attention_pop_ups")}
              <InfoTooltip text={t("widget_pop_ups_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <div className="control-group">
          <div className="delay-seconds-container">
            <FormControlLabel
              control={
                <Switch
                  checked={widgetStyles.popUpMessagesActive}
                  onChange={(e) =>
                    handleStyleChange("popUpMessagesActive", e.target.checked)
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#5616ea" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#5616ea",
                    },
                    "& .MuiSwitch-track": { backgroundColor: "#ccc" },
                  }}
                />
              }
              label={t("activate_pop_up_messages")}
            />
            {widgetStyles.popUpMessagesActive && (
            <TextField
              label={t("delay_seconds")}
              type="number"
              className="delay-seconds-input"
              value={widgetStyles.popUpDelaySeconds}
              onChange={(e) =>
                handleStyleChange(
                  "popUpDelaySeconds",
                  parseInt(e.target.value, 10) || 0
                )
              }
              variant="outlined"
              inputProps={{ style: { padding: "8px 10px" } }}
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                  border: "1px solid #515151",
                },
                "& .MuiInputLabel-root": {
                  color: "gray",
                  "&.Mui-focused": { color: "#5616ea" },
                },
              }}
            />
            )}
          </div>
          {/* The hosted Webflow widget leaves timed pop-ups out (Webflow guideline). */}
          {isWebflow && <p className="widget-pop-ups-webflow-note">{t("widget_pop_ups_webflow_note")}</p>}
        </div>

          </AccordionDetails>
        </Accordion>

        {/* ---------- predefined questions (array, max 3) ---------- */}
        <Accordion expanded={!!expandedAccordions.predefinedQuestions} onChange={toggleAccordion("predefinedQuestions")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("edit_predefined_questions")}
              <InfoTooltip text={t("widget_predefined_questions_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <div className="control-group">

          <div className="welcome-messages-parent">
            {widgetStyles.predefinedQuestions.map((q, idx) => (
              <Box key={idx} marginBottom={-0.8}>
                <TextField
                  label={`${t("predefined_question")} ${idx + 1}`}
                  value={q}
                  onChange={(e) => handlePredefinedChange(idx, e.target.value)}
                  fullWidth
                  multiline
                  variant="outlined"
                  inputProps={{ maxLength: MAX_PREDEFINED_CHARS }}
                  helperText={`${q.length}/${MAX_PREDEFINED_CHARS}`}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: "right",
                      mr: 0,
                      color:
                        q.length >= MAX_PREDEFINED_CHARS
                          ? "error.main"
                          : "text.secondary",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t("remove")}
                          onClick={() => removePredefinedQuestion(idx)}
                          edge="end"
                          size="small"
                          sx={{ color: "text.secondary" }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      border: "1px solid #515151",
                    },
                    "& .MuiInputLabel-root": {
                      color: "gray",
                      "&.Mui-focused": { color: "#5616ea" },
                    },
                  }}
                />
              </Box>
            ))}
          </div>

          <button
            onClick={addPredefinedQuestion}
            className="add-email-button"
            title={t("add")}
            disabled={widgetStyles.predefinedQuestions.length >= 3}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          {widgetStyles.predefinedQuestions.length >= 3 && (
            <Typography variant="caption" color="textSecondary">
              {t("max_three_reached")}
            </Typography>
          )}
        </div>

          </AccordionDetails>
        </Accordion>

        <Accordion expanded={!!expandedAccordions.inputPlaceholder} onChange={toggleAccordion("inputPlaceholder")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("widget_input_section")}
              <InfoTooltip text={t("widget_input_placeholder_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        {/* ---------- input placeholder ---------- */}
        <div className="control-group" style={{marginBottom: '0'}}>
          <TextField
            id="placeholder-text-input"
            label={t("edit_input_placeholder")}
            value={widgetStyles.inputPlaceholder}
            onChange={(e) => handleStyleChange("inputPlaceholder", e.target.value)}
            multiline
            variant="outlined"
            inputProps={{ maxLength: MAX_INPUT_PLACEHOLDER_CHARS }}
            helperText={`${inputPlaceholderCharsUsed}/${MAX_INPUT_PLACEHOLDER_CHARS}`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                mr: 0,
                color: inputPlaceholderAtLimit ? "error.main" : "text.secondary",
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                border: "1px solid #515151",
              },
              "& .MuiInputLabel-root": {
                color: "gray",
                "&.Mui-focused": { color: "#5616ea" },
              },
            }}
          />
        </div>

        {/* ---------- voice input: from Boost up; switching it on below that opens the upgrade modal ---------- */}
        <div className="control-group" style={{ marginTop: 12, marginBottom: 0 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!widgetStyles.voiceInput}
                onChange={(e) => {
                  if (e.target.checked && !voicePlanOk) {
                    setVoiceUpgradeOpen(true);
                    return;
                  }
                  handleStyleChange("voiceInput", e.target.checked);
                }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#5616ea" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#5616ea" },
                  "& .MuiSwitch-track": { backgroundColor: "#ccc" },
                }}
              />
            }
            label={
              // the name, then the widget's own microphone (owner, 03.11.2026: no explanation needed)
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {t("widget_voice_input_label")}
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#5616ea" }}>
                  <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" />
                </svg>
              </span>
            }
          />
          {widgetStyles.voiceInput && !voicePlanOk && (
            <Typography variant="caption" display="block" sx={{ color: "warning.dark", mt: 0.25 }}>
              {t("widget_voice_input_plan_hint")}
            </Typography>
          )}
        </div>

          </AccordionDetails>
        </Accordion>

        {/* ---------- theme color ---------- */}
        <Accordion expanded={!!expandedAccordions.colors} onChange={toggleAccordion("colors")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("colors")}
              <InfoTooltip text={t("widget_colors_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <div className="control-group">
          <div className="color-picker-container">
            <label
              className="widget-setting-title"
              htmlFor="themeColorPicker"
            >
              {t("widget_theme_color_label")}
            </label>
            <input
              id="themeColorPicker"
              type="color"
              value={widgetStyles.themeColor}
              onChange={(e) =>
                handleStyleChange("themeColor", e.target.value)
              }
            />
          </div>
        </div>

        {/* ---------- header font color ---------- */}
        <div className="control-group">
          <div className="color-picker-container">
            <label className="widget-setting-title">{t("widget_header_font_color_label")}</label>
            <input
              type="color"
              value={widgetStyles.headerColor}
              onChange={(e) =>
                handleStyleChange("headerColor", e.target.value)
              }
            />
          </div>
        </div>

        {/* ---------- hover color ---------- */}
        <div className="control-group">
          <div className="color-picker-container">
            <label className="widget-setting-title">{t("widget_button_hover_color_label")}</label>
            <input
              type="color"
              value={widgetStyles.hoverColor}
              onChange={(e) =>
                handleStyleChange("hoverColor", e.target.value)
              }
            />
          </div>
        </div>

          </AccordionDetails>
        </Accordion>

        {/* ---------- remove powered by ---------- */}
        <Accordion expanded={!!expandedAccordions.branding} onChange={toggleAccordion("branding")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("branding")}
              <InfoTooltip text={t("widget_branding_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <div className="control-group">
          <FormControlLabel
            control={
              <Switch
                checked={widgetStyles.removePoweredBy}
                onChange={(e) => {
                  const plan = bot?.subscription_plan?.name;
                  const hasAccess = plan === "Boost" || plan === "Ultimo";

                  if (!hasAccess) {
                    setUpgradeModalOpen(true);
                    return;
                  }

                  handleStyleChange("removePoweredBy", e.target.checked);
                }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#5616ea" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#5616ea",
                  },
                  "& .MuiSwitch-track": { backgroundColor: "#ccc" },
                }}
              />
            }
            label={t("widget_remove_powered_by_label")}
          />
        </div>

        {widgetStyles.removePoweredBy && (
          <div className="control-group" style={{ marginTop: 12 }}>
            <label
              htmlFor="customBrandingTextInput"
              style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {t("widget_custom_branding_text_label")}
              <InfoTooltip text={t("widget_custom_branding_text_tooltip")} />
            </label>
            <TextField
              id="customBrandingTextInput"
              size="small"
              fullWidth
              value={widgetStyles.customBrandingText || ''}
              onChange={(e) => {
                const plan = bot?.subscription_plan?.name;
                const hasAccess = plan === "Boost" || plan === "Ultimo";
                if (!hasAccess) {
                  setUpgradeModalOpen(true);
                  return;
                }
                handleStyleChange("customBrandingText", e.target.value);
              }}
              placeholder={t("widget_custom_branding_text_ph")}
              inputProps={{ maxLength: 80 }}
              helperText={`${(widgetStyles.customBrandingText || '').length}/80`}
            />
          </div>
        )}

          </AccordionDetails>
        </Accordion>

        {/* ---------- horizontal alignment ---------- */}
        <Accordion expanded={!!expandedAccordions.widget} onChange={toggleAccordion("widget")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("widget")}
              <InfoTooltip text={t("widget_appearance_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <div className="control-group">
          <div className="color-picker-container">
            <label className="widget-setting-title">
              {t("widget_horizontal_alignment")}
              <FontAwesomeIcon
                icon={faArrowsLeftRight}
                className="alignment-icon"
              />
            </label>
            <div className="radio-buttons">
              <div className="radio-buttons-content">
                <input
                  type="radio"
                  id="leftAlignment"
                  name="widgetHorizontalAlignment"
                  value="left"
                  checked={widgetStyles.widgetHorizontalAlignment === "left"}
                  onChange={(e) =>
                    handleStyleChange(
                      "widgetHorizontalAlignment",
                      e.target.value
                    )
                  }
                />
                <label htmlFor="leftAlignment">{t("left")}</label>
              </div>
              <div className="radio-buttons-content">
                <input
                  type="radio"
                  id="rightAlignment"
                  name="widgetHorizontalAlignment"
                  value="right"
                  checked={widgetStyles.widgetHorizontalAlignment === "right"}
                  onChange={(e) =>
                    handleStyleChange(
                      "widgetHorizontalAlignment",
                      e.target.value
                    )
                  }
                />
                <label htmlFor="rightAlignment">{t("right")}</label>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- vertical alignment ---------- */}
        <div className="control-group">
          <div className="color-picker-container">
            <label className="widget-setting-title">
              {t("widget_vertical_alignment")}
              <FontAwesomeIcon
                icon={faArrowsUpDown}
                className="alignment-icon"
              />
            </label>
            <div className="radio-buttons">
              <div className="radio-buttons-content">
                <input
                  type="radio"
                  id="bottomAlignment"
                  name="widgetVerticalAlignment"
                  value="bottom"
                  checked={widgetStyles.widgetVerticalAlignment === "bottom"}
                  onChange={(e) =>
                    handleStyleChange("widgetVerticalAlignment", e.target.value)
                  }
                />
                <label htmlFor="bottomAlignment">{t("bottom")}</label>
              </div>
              <div className="radio-buttons-content">
                <input
                  type="radio"
                  id="elevatedAlignment"
                  name="widgetVerticalAlignment"
                  value="elevated"
                  checked={widgetStyles.widgetVerticalAlignment === "elevated"}
                  onChange={(e) =>
                    handleStyleChange("widgetVerticalAlignment", e.target.value)
                  }
                />
                <label htmlFor="elevatedAlignment">{t("elevated")}</label>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- pulsing ---------- */}
        <div className="control-group">
          <div className="color-picker-container">
            <label className="widget-setting-title">{t("enable_pulsing")}</label>
            <div className="radio-buttons">
              <div className="radio-buttons-content">
                <input
                  type="radio"
                  id="pulsing-enabled"
                  name="pulsing"
                  value="enabled"
                  checked={widgetStyles.pulsing === true}
                  onChange={() => handleStyleChange("pulsing", true)}
                />
                <label htmlFor="pulsing-enabled">{t("enabled")}</label>
              </div>
              <div className="radio-buttons-content">
                <input
                  type="radio"
                  id="pulsing-disabled"
                  name="pulsing"
                  value="disabled"
                  checked={widgetStyles.pulsing === false}
                  onChange={() => handleStyleChange("pulsing", false)}
                />
                <label htmlFor="pulsing-disabled">{t("disabled")}</label>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- widget size slider ---------- */}
        <div className="control-group">
          <label>
            <div className="border-radius-container">
              <div className="widget-setting-title">{t("widget_size_label")}</div>
              <div>
                <input
                  type="range"
                  min="40"
                  max="100"
                  value={widgetStyles.widgetSize}
                  onChange={(e) =>
                    handleStyleChange(
                      "widgetSize",
                      parseInt(e.target.value, 10)
                    )
                  }
                />
              </div>
            </div>
          </label>
        </div>

        {/* ---------- border radius slider ---------- */}
        <div className="control-group">
          <label>
            <div className="border-radius-container">
              <div className="widget-setting-title">{t("widget_border_radius_label")}</div>
              <div>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={widgetStyles.widgetBorderRadius}
                  onChange={(e) =>
                    handleStyleChange(
                      "widgetBorderRadius",
                      parseInt(e.target.value, 10)
                    )
                  }
                />
              </div>
            </div>
          </label>
        </div>


        {/* ---------- upload widget icon ---------- */}
        <div className="control-group">
          <div className="upload-with-tooltip">
            <label className="file-upload">
              <div className="upload-icon-text">
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
                <p>{t("widget_upload_icon_label")}</p>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={iconInputRef}
                onChange={(e) => handleUpload(e, "icon")}
              />
            </label>
            <InfoTooltip text={t("widget_upload_icon_tooltip")} />
          </div>
          {widgetStyles.icon && (
            <div className="uploaded-file-info">
              <span>{uploadedIconFile ? uploadedIconFile.name : t("widget_existing_icon")}</span>
              {typeof widgetStyles.icon === 'string' && widgetStyles.icon.startsWith('data:') && (
                <button
                  onClick={() => handleEditExistingFile("icon")}
                  className="remove-file-btn"
                  title={t("widget_edit_recrop")}
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
              )}
              <button
                onClick={() => handleRemoveFile("icon")}
                className="remove-file-btn"
                title={t("widget_remove_file")}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          )}
        </div>

        {/* ---------- upload header logo ---------- */}
        <div className="control-group">
          <div className="upload-with-tooltip">
            <label className="file-upload">
              <div className="upload-icon-text">
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
                <p>{t("widget_upload_logo_label")}</p>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={logoInputRef}
                onChange={(e) => handleUpload(e, "logo")}
              />
            </label>
            <InfoTooltip text={t("widget_upload_logo_tooltip")} />
          </div>
          {widgetStyles.logo && (
            <div className="uploaded-file-info">
              <span>{uploadedLogoFile ? uploadedLogoFile.name : t("widget_existing_logo")}</span>
              {typeof widgetStyles.logo === 'string' && widgetStyles.logo.startsWith('data:') && (
                <button
                  onClick={() => handleEditExistingFile("logo")}
                  className="remove-file-btn"
                  title={t("widget_edit_recrop")}
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
              )}
              <button
                onClick={() => handleRemoveFile("logo")}
                className="remove-file-btn"
                title={t("widget_remove_file")}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          )}
        </div>

        {/* ---------- upload avatar icon ---------- */}
        <div className="control-group">
          <div className="upload-with-tooltip">
            <label className="file-upload">
              <div className="upload-icon-text">
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
                <p>{t("widget_upload_avatar_label")}</p>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={avatarInputRef}
                onChange={(e) => handleUpload(e, "avatar")}
              />
            </label>
            <InfoTooltip text={t("widget_upload_avatar_tooltip")} />
          </div>
          {widgetStyles.avatar && (
            <div className="uploaded-file-info">
              <span>{uploadedAvatarFile ? uploadedAvatarFile.name : t("widget_existing_avatar")}</span>
              {typeof widgetStyles.avatar === 'string' && widgetStyles.avatar.startsWith('data:') && (
                <button
                  onClick={() => handleEditExistingFile("avatar")}
                  className="remove-file-btn"
                  title={t("widget_edit_recrop")}
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
              )}
              <button
                onClick={() => handleRemoveFile("avatar")}
                className="remove-file-btn"
                title={t("widget_remove_file")}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          )}
        </div>

        {/* ---------- font family ---------- */}
        <div className="control-group">
          <FormControl
            sx={{
              marginTop: "5px",
              maxWidth: "300px",
              width: "100%",
              "& label.Mui-focused": {
                color: "#5616ea",
              },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  border: "1px solid #515151",
                },
              },
            }}
          >
            <InputLabel id="font-family-select-label">{t("widget_font_family_label")}</InputLabel>
            <Select
              labelId="font-family-select-label"
              value={widgetStyles.fontFamily}
              onChange={(e) => handleStyleChange("fontFamily", e.target.value)}
              label={t("widget_font_family_label")}
              className="bot-select"
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 320 },
                },
              }}
            >
              {availableFontOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  className="bot-select-item"
                  style={{ fontFamily: option.value }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!selectedFontConfig && widgetStyles.fontFamily && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              {t("widget_using_custom_font", { font: widgetStyles.fontFamily })}
            </Typography>
          )}
        </div>

          </AccordionDetails>
        </Accordion>

        {/* ---------- enforce user details (widget pre-chat gate) ----------
            Self-contained + auto-saving (persists to widget_config via the
            warm_lead_function endpoints), so it lives here as a sibling
            accordion but stays out of this tab's save/unsaved-changes flow. */}
        <EnforceUserDetails
          requirePreChat={!!widgetStyles.requirePreChat}
          requiredFields={widgetStyles.requiredFields || []}
          parameters={leadParameters}
          anyLanguageNote={!!widgetStyles.anyLanguageNote}
          fieldLabels={widgetStyles.fieldLabels || {}}
          onToggleAnyLanguage={handleToggleAnyLanguage}
          onEditFieldLabel={handleEditFieldLabel}
          onToggleRequire={handleToggleRequirePreChat}
          onToggleField={handleToggleRequiredField}
          onAddParameter={handleAddLeadParameter}
          onDeleteParameter={handleDeleteLeadParameter}
        />

        {/* ---------- link behavior ---------- */}
        <Accordion className="widget-accordion-last" expanded={!!expandedAccordions.linkBehavior} onChange={toggleAccordion("linkBehavior")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("link_behavior")}
              <InfoTooltip text={t("widget_link_behavior_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <div className="control-group">
          <FormControlLabel
            control={
              <Switch
                checked={widgetStyles.openLinksInNewTab}
                onChange={(e) =>
                  handleStyleChange("openLinksInNewTab", e.target.checked)
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#5616ea" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#5616ea",
                  },
                  "& .MuiSwitch-track": { backgroundColor: "#ccc" },
                }}
              />
            }
            label={t("open_links_in_new_tab")}
          />
        </div>

          </AccordionDetails>
        </Accordion>

        {/* ---------- unsaved / save / discard ---------- */}
        {hasUnsavedChanges && (
          <div className="unsaved-changes-message">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">{t("unsaved_changes_info")}</div>
          </div>
        )}
        <div className="save-discard-buttons">
          <button
            onClick={handleSaveWidgetConfiguration}
            className="save-widget-btn"
          >
            {t("save")}
          </button>
          {hasUnsavedChanges && (
            <button
              onClick={handleDiscardChanges}
              className="discard-widget-btn"
            >
              {t("discard_changes")}
            </button>
          )}
        </div>
      </div>

      {/* ---------- live preview ---------- */}
      <div className="preview-container">
        <WidgetPreview bot={bot} widgetStyles={widgetStyles} leadParameters={leadParameters} onScreenshotRefresh={handleScreenshotRefresh} />
      </div>

      {UpgradeModal}
    </div>
  );
};

export default WidgetCustomizationTab;
